/**
 * LE CARNET DE BORD, MIS EN PHRASES (§13.1).
 *
 * Il était rédigé en français DANS LA BASE — « Repos (long) : 42 PV. » — et c'était la seule partie
 * de l'interface qu'aucune traduction ne pouvait rattraper : une phrase figée le jour où le geste a
 * eu lieu ne se retraduit pas. Le serveur écrit désormais un CODE et ses PARAMÈTRES ; la phrase se
 * compose ici, dans la langue du lecteur, à chaque affichage.
 *
 * CE QUI VIENT DE L'AVENTURE NE SE TRADUIT PAS. Le nom d'un objet, le libellé d'un coffre, le titre
 * d'un nœud, la cause d'une mort : ce sont des CITATIONS, écrites une fois dans la langue de
 * l'aventure. Les traduire à la volée reviendrait à réécrire le livre à chaque lecture. Elles
 * voyagent donc telles quelles, et l'on ne les passe jamais à `t()`.
 *
 * LES LIGNES D'AVANT LA MIGRATION 013 n'ont pas de code : elles portent la phrase française qu'on
 * leur a écrite, et on l'affiche telle quelle. On ne réécrit pas l'histoire d'un joueur pour la
 * beauté d'un schéma.
 */
import type { LigneDeJournal } from "../api/types.js";
import { t } from "./langue.js";
import { BLESSURES, CARACTERISTIQUES, ETATS, REPOS } from "./mots.js";
import type { Blessure, Caracteristique, Etat } from "../api/types.js";

/** Un paramètre, ramené au texte. Le serveur n'envoie que des scalaires ; `null` se lit « rien ». */
function mot(valeur: unknown): string {
  return valeur === null || valeur === undefined ? "" : String(valeur);
}

function nombre(valeur: unknown): number {
  return typeof valeur === "number" ? valeur : Number(valeur ?? 0);
}

/** Un état, avec son nom d'interface — traduit, lui : « saignement » est un mot du jeu, pas une
 * citation de l'aventure. */
function nomDEtat(code: string): string {
  const connu = ETATS[code as Etat];
  return connu ? t(connu.nom).toLowerCase() : code;
}

/** La liste d'états d'un paramètre : le serveur les joint par des virgules pour rester scalaire. */
function listeDEtats(brut: unknown): string {
  return mot(brut)
    .split(",")
    .filter(Boolean)
    .map(nomDEtat)
    .join(", ");
}

/**
 * CE QUE DIT UNE LIGNE. Le retour est une phrase prête à lire ; une ligne dont le code serait
 * inconnu — un serveur plus récent que le front — rend son code plutôt que de disparaître : une
 * trace muette est une trace qu'on ne peut ni lire ni signaler.
 */
export function ditLaLigne(ligne: LigneDeJournal): string {
  if (!ligne.code) return ligne.resume ?? "";
  const p = ligne.params ?? {};

  switch (ligne.code) {
    /* --- Le fil de l'aventure ------------------------------------------- */
    case "aventure_commence":
      return t("L'aventure commence : {titre}.", { titre: mot(p.titre) });
    case "aventure_achevee":
      return t("L'aventure s'achève.");
    // LE TITRE D'UN NŒUD EST L'AVENTURE ELLE-MÊME : aucune phrase autour, aucune traduction.
    case "noeud":
      return mot(p.titre);
    case "ancre_prise":
      return t("Vous prenez vos repères : ce lieu servira de point de reprise.");
    case "pas_seul":
      return t("Vous n'êtes pas seul ici.");
    case "reprise":
      return t("Reprise à l'ancre. Cicatrice : {quoi}.", {
        quoi: t(CARACTERISTIQUES[mot(p.cicatrice) as Caracteristique]?.nom ?? mot(p.cicatrice)).toLowerCase()
      });
    case "heritage":
      return t("{defunt} est tombé — {cause}. {successeur} reprend la route à l'ancre.", {
        defunt: mot(p.defunt),
        cause: mot(p.cause),
        successeur: mot(p.successeur)
      });
    case "mort":
      return t("Mort : {cause}.", { cause: mot(p.cause) });

    /* --- L'horloge et ses échéances (§3.4) -------------------------------- */
    // LE TITRE ET LA CONSÉQUENCE SONT DES CITATIONS (§13.1) : ils viennent de l'aventure, ils ont
    // été écrits dans sa langue, et ils traversent la traduction sans y toucher. Seul le cadre
    // autour d'eux se traduit.
    case "echeance":
      return t("{titre} : {consequence}", { titre: mot(p.titre), consequence: mot(p.consequence) });

    /* --- Le corps (§3.4) ------------------------------------------------- */
    case "corps":
      return ditLeCorps(mot(p.quoi));
    case "repas":
      return t("Repas.");
    case "repos": {
      const nom = REPOS[mot(p.type)]?.nom ?? mot(p.type);
      const etats = listeDEtats(p.etats);
      return etats
        ? t("{repos} : {pv} PV. On vous a soigné : {etats}.", { repos: t(nom), pv: nombre(p.pv), etats })
        : t("{repos} : {pv} PV.", { repos: t(nom), pv: nombre(p.pv) });
    }
    case "rebouteux":
      return t("Le rebouteux referme ce qui pouvait l'être ({blessure}) : {prix} or.", {
        blessure: t(BLESSURES[mot(p.blessure) as Blessure]?.nom ?? mot(p.blessure)).toLowerCase(),
        prix: nombre(p.prix)
      });
    case "objet_utilise": {
      const dits = effetsDUnConsommable(p);
      return t("{nom} : {effet}.", { nom: mot(p.nom), effet: dits || t("sans effet") });
    }
    case "parchemin_lu": {
      const dits = effetsDUnParchemin(p);
      return t("{nom} : {effet}.", { nom: mot(p.nom), effet: dits || t("sans effet") });
    }
    case "flamme_allumee":
      return t("{nom} : {heures} h de lumière.", { nom: mot(p.nom), heures: nombre(p.heures) });
    case "flamme_eteinte":
      return t("Vous étouffez la flamme.");

    /* --- Fouiller (§3.6) -------------------------------------------------- */
    case "cache_fouillee":
      return nombre(p.or) > 0
        ? t("Vous fouillez {quoi}. {or} or.", { quoi: mot(p.quoi), or: nombre(p.or) })
        : t("Vous fouillez {quoi}.", { quoi: mot(p.quoi) });
    case "don_recu":
      return nombre(p.or) > 0
        ? t("On vous tend {quoi}. {or} or.", { quoi: mot(p.quoi), or: nombre(p.or) })
        : t("On vous tend {quoi}.", { quoi: mot(p.quoi) });
    case "objets_pris":
      return t("{combien} objet(s) pris dans {quoi}.", { combien: nombre(p.combien), quoi: mot(p.quoi) });
    case "fouille_trouve":
      return t("Vous fouillez les lieux : {combien} chose(s) que personne n'avait vue.", {
        combien: nombre(p.combien)
      });
    case "fouille_rien":
      return t("Vous fouillez les lieux, méthodiquement. Il n'y a rien de plus.");
    case "fouille_aveugle":
      return t("Vous fouillez à tâtons, et vous ne trouvez rien : il faudrait de la lumière.");
    case "enigme_juste":
      return t("L'inscription vous donne raison.");
    case "enigme_fausse":
      return t("Vous tournez autour de l'inscription, sans trouver.");

    /* --- Les dés (§3.2, §3.9) --------------------------------------------- */
    case "jet_reussi":
    case "jet_rate": {
      const stat = t(CARACTERISTIQUES[mot(p.stat) as Caracteristique]?.nom ?? mot(p.stat)).toLowerCase();
      return t("{quoi} — {stat} {de}+{bonus} contre {dc} : {issue}.", {
        quoi: mot(p.quoi),
        stat,
        de: nombre(p.de),
        bonus: nombre(p.bonus),
        dc: nombre(p.dc),
        issue: ligne.code === "jet_reussi" ? t("réussi") : t("raté")
      });
    }
    case "piege_evite":
      return t("{nom} : vous le voyez à temps ({jet}+{bonus} contre {dc}).", {
        nom: mot(p.nom),
        jet: nombre(p.jet),
        bonus: nombre(p.bonus),
        dc: nombre(p.dc)
      });
    case "piege_subi": {
      const etat = mot(p.etat) ? `, ${nomDEtat(mot(p.etat))}` : "";
      return (
        t("{nom} : {jet}+{bonus} contre {dc}, {degats} dégâts", {
          nom: mot(p.nom),
          jet: nombre(p.jet),
          bonus: nombre(p.bonus),
          dc: nombre(p.dc),
          degats: nombre(p.degats)
        }) + `${etat}.`
      );
    }
    case "degats":
      return t("{degats} dégâts.", { degats: nombre(p.degats) });
    case "etat_pose":
      return t("État : {etat}.", { etat: nomDEtat(mot(p.etat)) });

    /* --- Le combat (§3.2) -------------------------------------------------- */
    case "combat_engage":
      return t("Combat engagé : {combien} adversaire(s).", { combien: nombre(p.combien) });
    case "victoire":
      return t("Victoire en {tours} tour(s). {or} or.", { tours: nombre(p.tours), or: nombre(p.or) });
    case "fuite":
      return t("Vous fuyez.");
    case "corps_a_fouiller":
      return t("Il reste un corps à fouiller.");

    default:
      return ligne.resume ?? ligne.code;
  }
}

/** CE QUE LE CORPS DIT (§3.4). Les événements d'horloge sont des codes depuis le lot 1 — ils n'ont
 * jamais été des phrases —, ce qui est exactement pourquoi le journal traduisible n'a rien eu à
 * inventer de ce côté-là. */
function ditLeCorps(quoi: string): string {
  switch (quoi) {
    case "faim":
      return t("La faim se fait sentir.");
    case "famine":
      return t("La famine vous prend des forces.");
    case "fatigue_malus":
      return t("La fatigue pèse sur tous vos gestes.");
    case "effondrement":
      return t("Vous ne tenez plus debout.");
    case "torche_eteinte":
      return t("Votre flamme s'éteint.");
    case "benediction_eteinte":
      return t("Ce qui vous portait s'éteint.");
    default:
      return t("Le corps parle : {quoi}.", { quoi });
  }
}

function effetsDUnConsommable(p: Record<string, unknown>): string {
  const dits: string[] = [];
  if (nombre(p.pv) > 0) dits.push(t("+{n} PV", { n: nombre(p.pv) }));
  if (nombre(p.essence) > 0) dits.push(t("+{n} essence", { n: nombre(p.essence) }));
  const etats = listeDEtats(p.etats);
  if (etats) dits.push(t("{etats} soigné(s)", { etats }));
  if (p.nourrit === true) dits.push(t("repas"));
  return dits.join(", ");
}

function effetsDUnParchemin(p: Record<string, unknown>): string {
  const dits: string[] = [];
  if (mot(p.blessure)) {
    dits.push(t("{blessure} refermée", { blessure: t(BLESSURES[mot(p.blessure) as Blessure]?.nom ?? mot(p.blessure)).toLowerCase() }));
  }
  const etats = listeDEtats(p.etats);
  if (etats) dits.push(t("{etats} dissipé(s)", { etats }));
  if (p.benediction !== null && p.benediction !== undefined) {
    dits.push(t("béni pour {heures} h", { heures: nombre(p.benediction) }));
  }
  if (p.revelees !== null && p.revelees !== undefined) {
    dits.push(
      nombre(p.revelees) > 0
        ? t("{n} chose(s) que personne n'avait vue", { n: nombre(p.revelees) })
        : t("rien de caché ici")
    );
  }
  if (p.dessinees !== null && p.dessinees !== undefined) {
    dits.push(t("{n} case(s) portées sur la carte", { n: nombre(p.dessinees) }));
  }
  return dits.join(", ");
}
